import { View, Text, TouchableOpacity, Modal, Alert } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { AgendaEvent, CalendarProvider } from "@/types";
import {
  exportToCalendar,
  getAvailableCalendarProviders,
  createCalendarExportConfig,
} from "../utils/calendar-export";

/**
 * Props for CalendarExportSheet
 */
interface CalendarExportSheetProps {
  visible: boolean;
  onClose: () => void;
  event: AgendaEvent | null;
}

/**
 * CalendarExportSheet Component
 * Bottom sheet for exporting events to external calendars
 */
export const CalendarExportSheet = ({
  visible,
  onClose,
  event,
}: CalendarExportSheetProps) => {
  const colors = useThemeColors();
  const providers = getAvailableCalendarProviders();

  /**
   * Handle calendar provider selection
   */
  const handleSelectProvider = async (provider: CalendarProvider) => {
    if (!event) return;

    const config = {
      ...createCalendarExportConfig(event),
      provider,
    };

    try {
      const success = await exportToCalendar(config, provider);
      if (success) {
        onClose();
      } else {
        Alert.alert(
          "Kan agenda niet openen",
          "Controleer of de agenda-app is geïnstalleerd."
        );
      }
    } catch (error) {
      console.error("Error exporting to calendar:", error);
      Alert.alert("Fout", "Kon evenement niet exporteren naar agenda.");
    }
  };

  if (!event) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <TouchableOpacity
        className="flex-1"
        style={{ backgroundColor: colors.overlay }}
        activeOpacity={1}
        onPress={onClose}
      >
        <View className="flex-1 justify-end">
          <TouchableOpacity activeOpacity={1}>
            <View
              className="rounded-t-3xl pb-8 pt-4"
              style={{ backgroundColor: colors.surface }}
            >
              {/* Handle indicator */}
              <View className="items-center mb-4">
                <View
                  className="w-10 h-1 rounded-full"
                  style={{ backgroundColor: colors.border }}
                />
              </View>

              {/* Header */}
              <View className="px-6 mb-4">
                <Text className="text-lg font-bold" style={{ color: colors.text }}>
                  Toevoegen aan agenda
                </Text>
                <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  Kies een agenda om &quot;{event.title}&quot; aan toe te voegen
                </Text>
              </View>

              {/* Provider options */}
              <View className="px-4">
                {providers.map((item, index) => (
                  <TouchableOpacity
                    key={item.provider}
                    onPress={() => handleSelectProvider(item.provider)}
                    className="flex-row items-center px-4 py-4 rounded-xl mb-2"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel={`Toevoegen aan ${item.label}`}
                    accessibilityRole="button"
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-4"
                      style={{ backgroundColor: colors.primaryLight }}
                    >
                      <Ionicons
                        name={item.icon as any}
                        size={20}
                        color={colors.primary}
                      />
                    </View>
                    <Text className="flex-1 font-medium" style={{ color: colors.text }}>
                      {item.label}
                    </Text>
                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={colors.textTertiary}
                    />
                  </TouchableOpacity>
                ))}
              </View>

              {/* Cancel button */}
              <TouchableOpacity
                onPress={onClose}
                className="mx-4 mt-4 py-4 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
                accessibilityLabel="Annuleren"
                accessibilityRole="button"
              >
                <Text className="font-semibold" style={{ color: colors.error }}>
                  Annuleer
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

export default CalendarExportSheet;

